import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { dateTime, inr, mobile as fmtMobile } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Chips, Field, Input, Modal, Notice, SearchInput, Textarea, useToast } from '../components/ui';
import type { BadgeTone } from '../components/ui';
import {
  GapDashboardView,
  GapEmployeeApplicationsTable,
  GapLinksTable,
  GapMembersTable,
  GapRegistrationsTable,
  GapTraceView,
  ReasonModal,
} from './GapProgramme';

type CommissionStatus = 'PENDING' | 'APPROVED' | 'PAID' | 'REJECTED' | 'ON_HOLD' | 'REVERSED';

interface CommissionRow {
  id: string;
  kind: 'CHAIN' | 'EMPLOYEE';
  amountPaise: number;
  status: CommissionStatus;
  createdAt: string;
  approvedAt: string | null;
  rejectedReason: string | null;
  paidAt: string | null;
  payoutRef: string | null;
  holdReason: string | null;
  reversedReason: string | null;
  reversedAfterPaid: boolean;
  earner: { code: string; type: string; name: string | null; mobile: string | null };
  referred: { name: string | null; partnerId: string | null; joinedAt: string; transactionId: string | null; paymentStatus: string | null };
}

interface GapIdRow {
  id: string;
  code: string;
  type: 'CUSTOMER' | 'EMPLOYEE';
  assignedAt: string | null;
  holder: { id: string; displayName: string | null; mobile: string } | null;
  directReferrals: number;
  commission: { totalEarnedPaise: number; pendingPaise: number; approvedPaise: number; paidPaise: number };
}

const STATUS: Record<CommissionStatus, { label: string; tone: BadgeTone }> = {
  PENDING: { label: 'Awaiting approval', tone: 'amber' },
  APPROVED: { label: 'Approved', tone: 'blue' },
  PAID: { label: 'Paid', tone: 'green' },
  REJECTED: { label: 'Rejected', tone: 'red' },
  ON_HOLD: { label: 'On hold', tone: 'neutral' },
  REVERSED: { label: 'Reversed', tone: 'red' },
};

type Dialog = { kind: 'reject' | 'paid' | 'hold' | 'reverse'; row: CommissionRow } | null;

/**
 * Global Axis Partner, run by its owner.
 *
 * Only the owner chooses Customer or Employee and generates the first links,
 * from the app; whoever joins through one becomes a member of that programme
 * once their ₹2,000 is verified, with a link of their own. Here: the owner's
 * dashboard, each programme's members, registrations and payments, the
 * referral tree and Sponsor ID tracking, the commission ledger, refunds and
 * reversals, suspension, and every link.
 *
 * A Global Customer ID earns ₹500 for its first direct referral (or each,
 * as the settings say); an Employee ID earns ₹100 for every direct referral.
 * Each commission is written when the referred member's fee is captured, and
 * waits here: approve it, then pay it by hand and record the reference. It
 * can be held, rejected, or reversed - a refunded payment reverses its own.
 * Every one of those is on the audit log.
 */
export function GapIdsPage() {
  const { get, set } = useListParams();
  const view = get('view') || 'dashboard';
  return (
    <div className="stack">
      <Notice tone="warn" title="Commissions are paid by hand">
        Nothing here pays anybody automatically. Approve a commission once the member's joining is genuine, pay it,
        then mark it paid with the bank or UPI reference. Commissions on registration fees were flagged for legal review
        alongside the partner bonuses; check before paying them out.
      </Notice>
      <Chips
        value={view}
        onChange={(v) => set('view', v)}
        options={[
          { value: 'dashboard', label: 'Owner dashboard' },
          { value: 'customers', label: 'Customer members' },
          { value: 'employees', label: 'Employee members' },
          { value: 'employee-registrations', label: 'Employee registrations' },
          { value: 'pending', label: 'Pending registrations' },
          { value: 'payments', label: 'Successful payments' },
          { value: 'trace', label: 'Referral tree & Sponsor IDs' },
          { value: 'commissions', label: 'Commission ledger' },
          { value: 'refunds', label: 'Refunds & reversals' },
          { value: 'links', label: 'Referral links' },
          { value: 'ids', label: 'All IDs' },
        ]}
      />
      {view === 'dashboard' && <GapDashboardView />}
      {view === 'customers' && <GapMembersTable key="c" type="CUSTOMER" />}
      {view === 'employees' && <GapMembersTable key="e" type="EMPLOYEE" />}
      {view === 'employee-registrations' && <GapEmployeeApplicationsTable />}
      {view === 'pending' && <GapRegistrationsTable key="p" initialStatus="CREATED" />}
      {view === 'payments' && <GapRegistrationsTable key="s" initialStatus="CAPTURED" />}
      {view === 'trace' && <GapTraceView />}
      {view === 'commissions' && <CommissionsTable />}
      {view === 'refunds' && (
        <>
          <GapRegistrationsTable key="r" initialStatus="REFUNDED" />
          <CommissionsTable fixedStatus="REVERSED" />
        </>
      )}
      {view === 'links' && <GapLinksTable />}
      {view === 'ids' && <IdsTable />}
    </div>
  );
}

function CommissionsTable({ fixedStatus }: { fixedStatus?: CommissionStatus } = {}) {
  const { get, set } = useListParams();
  const status = fixedStatus ?? get('status');
  const kind = get('kind');
  const [dialog, setDialog] = useState<Dialog>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['gap-commissions'] });

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/admin/gap/commissions/${id}/approve`),
    onSuccess: () => {
      toast('Approved', 'success');
      refresh();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const release = useMutation({
    mutationFn: (id: string) => api.post(`/admin/gap/commissions/${id}/release`),
    onSuccess: () => {
      toast('Released: waiting for approval again', 'success');
      refresh();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<CommissionRow>> = [
    {
      key: 'earner',
      header: 'Earned by',
      render: (r) => (
        <div>
          <div className="cell-strong cell-mono">{r.earner.code}</div>
          <div className="cell-muted">
            {r.earner.name ?? 'Unnamed'}
            {r.earner.mobile ? ` · ${fmtMobile(r.earner.mobile)}` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Rule',
      render: (r) => (r.kind === 'CHAIN' ? 'Customer ID · once' : 'Employee ID · per member'),
    },
    { key: 'amount', header: 'Amount', numeric: true, render: (r) => <span className="cell-strong">{inr(r.amountPaise)}</span> },
    {
      key: 'referred',
      header: 'For member',
      render: (r) => (
        <div>
          <div>{r.referred.name ?? '—'}</div>
          <div className="cell-muted">
            {r.referred.partnerId ?? ''} · joined {dateTime(r.referred.joinedAt)}
          </div>
          {r.referred.transactionId && (
            <div className="cell-muted cell-mono">
              {r.referred.transactionId}
              {r.referred.paymentStatus && r.referred.paymentStatus !== 'CAPTURED' ? ` · ${r.referred.paymentStatus.toLowerCase()}` : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <div>
          <Badge tone={STATUS[r.status].tone} dot>
            {STATUS[r.status].label}
          </Badge>
          {r.status === 'PAID' && r.payoutRef && <div className="cell-muted">Ref {r.payoutRef}</div>}
          {r.status === 'REJECTED' && r.rejectedReason && <div className="cell-muted">{r.rejectedReason}</div>}
          {r.status === 'ON_HOLD' && r.holdReason && <div className="cell-muted">{r.holdReason}</div>}
          {r.status === 'REVERSED' && (
            <div className="cell-muted">
              {r.reversedReason}
              {r.reversedAfterPaid && <div style={{ color: 'var(--danger)' }}>Was paid: recover it</div>}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="cell-actions" onClick={(e) => e.stopPropagation()}>
          {r.status === 'PENDING' && (
            <Button
              size="sm"
              variant="success"
              loading={approve.isPending && approve.variables === r.id}
              onClick={() => approve.mutate(r.id)}
            >
              Approve
            </Button>
          )}
          {r.status === 'APPROVED' && (
            <Button size="sm" variant="primary" onClick={() => setDialog({ kind: 'paid', row: r })}>
              Mark paid
            </Button>
          )}
          {(r.status === 'PENDING' || r.status === 'APPROVED') && (
            <Button size="sm" onClick={() => setDialog({ kind: 'hold', row: r })}>
              Hold
            </Button>
          )}
          {r.status === 'ON_HOLD' && (
            <Button size="sm" loading={release.isPending && release.variables === r.id} onClick={() => release.mutate(r.id)}>
              Release
            </Button>
          )}
          {(r.status === 'PENDING' || r.status === 'APPROVED' || r.status === 'ON_HOLD') && (
            <Button size="sm" variant="danger" onClick={() => setDialog({ kind: 'reject', row: r })}>
              Reject
            </Button>
          )}
          {r.status !== 'REJECTED' && r.status !== 'REVERSED' && (
            <Button size="sm" variant="danger" onClick={() => setDialog({ kind: 'reverse', row: r })}>
              Reverse
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable<CommissionRow>
        queryKey={['gap-commissions', status, kind]}
        path="/admin/gap/commissions"
        params={{ status, kind }}
        columns={columns}
        emptyIcon="₹"
        emptyTitle="No commissions yet"
        emptyBody="A commission appears here when somebody joins through a Customer or Employee ID and their fee is paid."
        footer={(page) => {
          const totals = page.meta.totals as Record<string, { count: number; amountPaise: number }> | undefined;
          if (!totals) return null;
          return (
            <div className="row row--wrap" style={{ padding: '12px var(--s5)', borderTop: '1px solid var(--border)', gap: 'var(--s5)' }}>
              {(Object.keys(STATUS) as CommissionStatus[])
                .filter((s) => totals[s])
                .map((s) => (
                  <span key={s} className="row" style={{ gap: 7 }}>
                    <Badge tone={STATUS[s].tone}>{STATUS[s].label}</Badge>
                    <strong>{inr(totals[s].amountPaise)}</strong>
                    <span className="cell-muted">({totals[s].count})</span>
                  </span>
                ))}
            </div>
          );
        }}
        toolbar={
          <div className="row row--wrap" style={{ gap: 'var(--s4)' }}>
            {!fixedStatus && (
              <Chips
                value={status}
                onChange={(v) => set('status', v)}
                options={[
                  { value: '', label: 'All' },
                  { value: 'PENDING', label: 'Awaiting approval' },
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'ON_HOLD', label: 'On hold' },
                  { value: 'PAID', label: 'Paid' },
                  { value: 'REJECTED', label: 'Rejected' },
                  { value: 'REVERSED', label: 'Reversed' },
                ]}
              />
            )}
            <Chips
              value={kind}
              onChange={(v) => set('kind', v)}
              options={[
                { value: '', label: 'Both' },
                { value: 'CHAIN', label: 'Customer ₹500' },
                { value: 'EMPLOYEE', label: 'Employee ₹100' },
              ]}
            />
          </div>
        }
      />
      {dialog && (dialog.kind === 'paid' || dialog.kind === 'reject') && (
        <ActionDialog
          dialog={dialog}
          onClose={() => setDialog(null)}
          onDone={() => {
            toast(dialog.kind === 'paid' ? 'Marked paid' : 'Rejected', 'success');
            setDialog(null);
            refresh();
          }}
        />
      )}
      {dialog && (dialog.kind === 'hold' || dialog.kind === 'reverse') && (
        <ReasonModal
          title={dialog.kind === 'hold' ? 'Hold commission' : 'Reverse commission'}
          sub={
            dialog.kind === 'hold'
              ? `${dialog.row.earner.code} · ${inr(dialog.row.amountPaise)}. Neither approved nor paid until released.`
              : `${dialog.row.earner.code} · ${inr(dialog.row.amountPaise)}. For a refunded, duplicate or fraudulent payment.${dialog.row.status === 'PAID' ? ' It was paid: it is marked to be recovered.' : ''}`
          }
          action={dialog.kind === 'hold' ? 'Hold' : 'Reverse'}
          path={`/admin/gap/commissions/${dialog.row.id}/${dialog.kind}`}
          onClose={() => setDialog(null)}
          onDone={() => {
            toast(dialog.kind === 'hold' ? 'Held' : 'Reversed', 'success');
            setDialog(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

function ActionDialog({ dialog, onClose, onDone }: { dialog: NonNullable<Dialog>; onClose: () => void; onDone: () => void }) {
  const [text, setText] = useState('');
  const toast = useToast();
  const paid = dialog.kind === 'paid';
  const send = useMutation({
    mutationFn: () =>
      api.post(`/admin/gap/commissions/${dialog.row.id}/${paid ? 'paid' : 'reject'}`, paid ? { reference: text.trim() } : { reason: text.trim() }),
    onSuccess: onDone,
    onError: (e: Error) => toast(e.message, 'error'),
  });
  return (
    <Modal
      title={paid ? 'Mark commission paid' : 'Reject commission'}
      sub={`${dialog.row.earner.code} · ${inr(dialog.row.amountPaise)}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant={paid ? 'primary' : 'danger'}
            disabled={text.trim().length < 3}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            {paid ? 'Mark paid' : 'Reject'}
          </Button>
        </>
      }
    >
      {paid ? (
        <Field label="Payment reference" hint="The bank or UPI reference of the payment you made.">
          <Input value={text} maxLength={120} onChange={(e) => setText(e.target.value)} />
        </Field>
      ) : (
        <Field label="Reason" hint="Kept with the commission. A rejected commission is not paid.">
          <Textarea rows={3} value={text} maxLength={300} onChange={(e) => setText(e.target.value)} />
        </Field>
      )}
    </Modal>
  );
}

function IdsTable() {
  const { get, set } = useListParams();
  const type = get('type');
  const search = get('search');
  const columns: Array<Column<GapIdRow>> = [
    { key: 'code', header: 'Partner ID', render: (r) => <span className="cell-strong cell-mono">{r.code}</span> },
    { key: 'type', header: 'Kind', render: (r) => (r.type === 'CUSTOMER' ? 'Global Customer ID' : 'Company Employee ID') },
    {
      key: 'holder',
      header: 'Holder',
      render: (r) =>
        r.holder ? (
          <div>
            <Link to={`/users/${r.holder.id}`} onClick={(e) => e.stopPropagation()}>
              {r.holder.displayName ?? 'Unnamed'}
            </Link>
            <div className="cell-muted">{fmtMobile(r.holder.mobile)}</div>
          </div>
        ) : (
          <span className="cell-muted">Unassigned</span>
        ),
    },
    { key: 'refs', header: 'Direct referrals', numeric: true, render: (r) => r.directReferrals },
    { key: 'earned', header: 'Earned', numeric: true, render: (r) => inr(r.commission.totalEarnedPaise) },
    { key: 'paid', header: 'Paid', numeric: true, render: (r) => inr(r.commission.paidPaise) },
  ];
  return (
    <DataTable<GapIdRow>
      queryKey={['gap-ids', type, search]}
      path="/admin/gap/ids"
      params={{ type, search }}
      columns={columns}
      emptyIcon="🆔"
      emptyTitle="No IDs yet"
      emptyBody="Every ID is its holder's Partner ID. The GAP ID manager assigns them in the app, and every new partner's Partner ID is their Customer ID too."
      toolbar={
        <div className="row row--wrap" style={{ gap: 'var(--s4)' }}>
          <Chips
            value={type}
            onChange={(v) => set('type', v)}
            options={[
              { value: '', label: 'Both' },
              { value: 'CUSTOMER', label: 'Customer IDs' },
              { value: 'EMPLOYEE', label: 'Employee IDs' },
            ]}
          />
          <SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Partner ID, name or mobile…" />
        </div>
      }
    />
  );
}
